import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TYPES_PRESTATION, STATUTS_PRESTATION, CRENEAUX } from '../../lib/prestations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCollaborateurs = () => {
  const [collabs, setCollabs] = useState([]);
  useEffect(() => { axios.get(`${API_URL}/formateurs`).then(r => setCollabs((r.data || []).filter(f => f.actif !== false))).catch(() => {}); }, []);
  return collabs;
};

const F = ({ label, children, className = '' }) => <div className={`space-y-1 ${className}`}><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;

// Identité de la prestation (type, intitulé, client, lieu, description, statut)
export const PrestationIdentite = ({ form, set, societes }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <F label="Type *">
      <Select value={form.type_prestation} onValueChange={v => set('type_prestation', v)}>
        <SelectTrigger data-testid="prestation-type"><SelectValue /></SelectTrigger>
        <SelectContent>{TYPES_PRESTATION.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
    </F>
    <F label="Statut">
      <Select value={form.statut} onValueChange={v => set('statut', v)}>
        <SelectTrigger data-testid="prestation-statut"><SelectValue /></SelectTrigger>
        <SelectContent>{STATUTS_PRESTATION.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
      </Select>
    </F>
    <F label="Intitulé de la mission *" className="md:col-span-2"><Input value={form.intitule} onChange={e => set('intitule', e.target.value)} placeholder="Ex. Audit sécurité entrepôt, Gestion administrative…" data-testid="prestation-intitule" /></F>
    <F label="Client (société)">
      <Select value={form.client_id || '__none'} onValueChange={v => { const s = societes.find(x => x.id === v); set('client_id', v === '__none' ? null : v); set('client', s ? s.nom : form.client); }}>
        <SelectTrigger data-testid="prestation-client"><SelectValue placeholder="Choisir une société" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="__none">— Saisie libre —</SelectItem>
          {societes.map(s => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
        </SelectContent>
      </Select>
    </F>
    <F label="Nom du client (si saisie libre)"><Input value={form.client || ''} onChange={e => set('client', e.target.value)} disabled={!!form.client_id} data-testid="prestation-client-libre" /></F>
    <F label="Contact"><Input value={form.contact || ''} onChange={e => set('contact', e.target.value)} placeholder="Nom, téléphone, email" /></F>
    <F label="Réf. / code client"><Input value={form.code_action_client || ''} onChange={e => set('code_action_client', e.target.value)} placeholder="Code porté sur les factures" /></F>
    <F label="Lieu d'intervention" className="md:col-span-2"><Input value={form.lieu || ''} onChange={e => set('lieu', e.target.value)} /></F>
    <F label="Description de la mission" className="md:col-span-2"><Textarea rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} data-testid="prestation-description" /></F>
  </div>
);

const CollabSelect = ({ value, onChange, collabs, testId }) => (
  <Select value={value || '__none'} onValueChange={v => { const c = collabs.find(x => x.id === v); onChange(v === '__none' ? null : v, c ? `${c.nom} ${c.prenom || ''}`.trim() : null); }}>
    <SelectTrigger className="h-8 text-xs" data-testid={testId}><SelectValue placeholder="Collaborateur" /></SelectTrigger>
    <SelectContent className="max-h-64">
      <SelectItem value="__none">— Aucun collaborateur —</SelectItem>
      {collabs.map(c => <SelectItem key={c.id} value={c.id}>{c.nom} {c.prenom || ''}</SelectItem>)}
    </SelectContent>
  </Select>
);

// Interventions : journées ou périodes
export const PrestationInterventions = ({ form, set, collabs }) => {
  const mode = form.mode_interventions || 'journees';
  const list = mode === 'journees' ? form.interventions : form.periodes;
  const key = mode === 'journees' ? 'interventions' : 'periodes';
  const upd = (i, patch) => set(key, list.map((x, j) => j === i ? { ...x, ...patch } : x));
  const del = (i) => set(key, list.filter((_, j) => j !== i));
  const add = () => set(key, [...list, mode === 'journees'
    ? { date: '', creneau: 'journee', collaborateur_id: null, collaborateur_nom: null, lieu: '', notes: '' }
    : { type: 'mensuel', date_debut: '', date_fin: '', collaborateur_id: null, collaborateur_nom: null, jours: '', heures: '', description: '' }]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500">Mode :</span>
        {[['journees', 'Journées d\'intervention'], ['periodes', 'Périodes (hebdo / mensuel)']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => set('mode_interventions', v)} className={`text-xs px-3 py-1 rounded-full border ${mode === v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-testid={`prestation-mode-${v}`}>{l}</button>
        ))}
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs ml-auto" onClick={add} data-testid="prestation-add-intervention"><Plus className="w-3 h-3 mr-1" /> {mode === 'journees' ? 'Journée' : 'Période'}</Button>
      </div>
      {list.length === 0 && <p className="text-xs text-slate-400 italic">Aucune {mode === 'journees' ? 'journée' : 'période'} saisie — la prestation peut rester sans collaborateur affecté.</p>}
      {mode === 'journees' ? list.map((it, i) => (
        <div key={i} className="grid grid-cols-[140px_120px_1fr_1fr_28px] gap-2 items-center" data-testid={`intervention-${i}`}>
          <Input type="date" className="h-8 text-xs" value={it.date} onChange={e => upd(i, { date: e.target.value })} data-testid={`intervention-date-${i}`} />
          <Select value={it.creneau || 'journee'} onValueChange={v => upd(i, { creneau: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{CRENEAUX.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
          <CollabSelect value={it.collaborateur_id} collabs={collabs} testId={`intervention-collab-${i}`} onChange={(id, nom) => upd(i, { collaborateur_id: id, collaborateur_nom: nom })} />
          <Input className="h-8 text-xs" placeholder="Lieu / notes" value={it.lieu || ''} onChange={e => upd(i, { lieu: e.target.value })} />
          <button type="button" onClick={() => del(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
        </div>
      )) : list.map((p, i) => (
        <div key={i} className="grid grid-cols-[110px_130px_130px_1fr_70px_70px_28px] gap-2 items-center" data-testid={`periode-${i}`}>
          <Select value={p.type || 'mensuel'} onValueChange={v => upd(i, { type: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="hebdo">Hebdo</SelectItem><SelectItem value="mensuel">Mensuel</SelectItem></SelectContent>
          </Select>
          <Input type="date" className="h-8 text-xs" value={p.date_debut} onChange={e => upd(i, { date_debut: e.target.value })} data-testid={`periode-debut-${i}`} />
          <Input type="date" className="h-8 text-xs" value={p.date_fin} onChange={e => upd(i, { date_fin: e.target.value })} data-testid={`periode-fin-${i}`} />
          <CollabSelect value={p.collaborateur_id} collabs={collabs} testId={`periode-collab-${i}`} onChange={(id, nom) => upd(i, { collaborateur_id: id, collaborateur_nom: nom })} />
          <Input type="number" min="0" step="0.5" className="h-8 text-xs" placeholder="Jours" title="Jours estimés" value={p.jours ?? ''} onChange={e => upd(i, { jours: e.target.value === '' ? null : Number(e.target.value) })} />
          <Input type="number" min="0" step="0.5" className="h-8 text-xs" placeholder="Heures" title="Heures estimées" value={p.heures ?? ''} onChange={e => upd(i, { heures: e.target.value === '' ? null : Number(e.target.value) })} />
          <button type="button" onClick={() => del(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
};
